/**
 * 業務通知テンプレート hooks (Phase A / 代案E, v3.2.21 で chat 統合)。
 *
 * Firestore 直 read (rules で editor+ 許可) + 書込は Callable 経由。
 * 新スキーマ: mail.{enabled,subject,body,fromAccountId} + chat.{enabled,type,text?,card?,endpointId?}
 */
import type {
  BusinessChatCardPayload,
  BusinessChatFormat,
  BusinessMailEvent,
  BusinessMailTemplate,
  BusinessNotificationAudience,
  BusinessNotificationTemplate,
} from '@goenhub/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { collection, doc, getDoc, getDocs } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

import { db, functions } from '@/lib/firebase';

/**
 * 既存コードからの参照のため、`BusinessMailTemplateRecord` の名前は維持。
 * 中身は新スキーマ `BusinessNotificationTemplate` + id + 旧フィールド互換 (移行期間)
 *
 * 2026-05-28: eventType を null 許容に変更したため、id は eventType と分離した UUID 等になり得る。
 */
export interface BusinessMailTemplateRecord
  extends Omit<BusinessNotificationTemplate, 'eventType'> {
  id: string;
  eventType: BusinessMailEvent | null;
  /** 旧 top-level フィールド (マイグレ前 docs 用、新規 docs では undefined) */
  enabled?: boolean;
  subject?: string;
  body?: string;
  fromAccountId?: string | null;
}

/** 旧 docs を新スキーマ形に正規化して返す (UI 側で常に新スキーマだけ読めるように)。 */
function normalize(
  id: string,
  raw: Record<string, unknown>,
): BusinessMailTemplateRecord {
  const rawMail = raw.mail as Partial<BusinessNotificationTemplate['mail']> | undefined;
  const rawChat = raw.chat as Partial<BusinessNotificationTemplate['chat']> | undefined;
  const legacy = raw as Partial<BusinessMailTemplate>;
  const mail = {
    enabled:
      typeof rawMail?.enabled === 'boolean'
        ? rawMail.enabled
        : !!legacy.enabled,
    subject:
      typeof rawMail?.subject === 'string'
        ? rawMail.subject
        : (legacy.subject ?? ''),
    body:
      typeof rawMail?.body === 'string' ? rawMail.body : (legacy.body ?? ''),
    fromAccountId:
      rawMail?.fromAccountId !== undefined
        ? (rawMail.fromAccountId ?? null)
        : (legacy.fromAccountId ?? null),
  };
  const chat = {
    enabled: !!rawChat?.enabled,
    type: (rawChat?.type === 'card' ? 'card' : 'text') as BusinessChatFormat,
    text: typeof rawChat?.text === 'string' ? rawChat.text : null,
    card: (rawChat?.card as BusinessChatCardPayload | null | undefined) ?? null,
    endpointId:
      typeof rawChat?.endpointId === 'string' ? rawChat.endpointId : null,
  };
  // 2026-05-28: 新フィールド (applicableCategoryIds / audience) を読み出す。
  // 空配列は null と等価扱い (= 共通) として正規化。
  const applicableRaw = raw.applicableCategoryIds;
  const applicableCategoryIds: string[] | null = Array.isArray(applicableRaw)
    ? (applicableRaw as string[]).filter(
        (x): x is string => typeof x === 'string' && x.length > 0,
      )
    : null;
  const audienceRaw = raw.audience;
  const audience: BusinessNotificationAudience | undefined =
    audienceRaw === 'customer' || audienceRaw === 'internal'
      ? audienceRaw
      : undefined;
  // 2026-05-28: eventType は null (未アサイン) も可
  // 旧 docID = eventType ドキュメント互換: eventType フィールドがなければ docID をフォールバック
  let resolvedEventType: BusinessMailEvent | null;
  const rawEventType = raw.eventType;
  if (rawEventType === null) {
    resolvedEventType = null;
  } else if (typeof rawEventType === 'string' && rawEventType.length > 0) {
    resolvedEventType = rawEventType as BusinessMailEvent;
  } else {
    resolvedEventType = id as BusinessMailEvent;
  }
  const label =
    typeof raw.label === 'string' && raw.label.length > 0 ? raw.label : null;
  return {
    id,
    eventType: resolvedEventType,
    label,
    mail,
    chat,
    availableVars: Array.isArray(raw.availableVars)
      ? (raw.availableVars as string[])
      : undefined,
    applicableCategoryIds:
      applicableCategoryIds && applicableCategoryIds.length > 0
        ? applicableCategoryIds
        : null,
    audience,
    updatedAt: raw.updatedAt as never,
    updatedBy: typeof raw.updatedBy === 'string' ? raw.updatedBy : '',
  };
}

export function useBusinessMailTemplates() {
  return useQuery({
    queryKey: ['business_mail_templates'],
    queryFn: async (): Promise<BusinessMailTemplateRecord[]> => {
      const snap = await getDocs(collection(db, 'mail_templates_business'));
      return snap.docs.map((d) => normalize(d.id, d.data()));
    },
  });
}

export function useBusinessMailTemplate(eventType: BusinessMailEvent | null) {
  return useQuery({
    queryKey: ['business_mail_templates', eventType],
    enabled: !!eventType,
    queryFn: async (): Promise<BusinessMailTemplateRecord | null> => {
      if (!eventType) return null;
      const snap = await getDoc(
        doc(db, 'mail_templates_business', eventType),
      );
      if (!snap.exists()) return null;
      return normalize(snap.id, snap.data() ?? {});
    },
  });
}

interface UpsertInput {
  /** 2026-05-28: 既存編集時は docID を渡す。新規・複製時は省略すれば UUID 自動採番 */
  templateId?: string;
  /** トリガー。null = 未アサイン */
  eventType: BusinessMailEvent | null;
  /** 2026-05-28: 複数テンプレ識別ラベル */
  label?: string | null;
  mail: {
    enabled: boolean;
    subject: string;
    body: string;
    fromAccountId?: string | null;
  };
  chat: {
    enabled: boolean;
    type: BusinessChatFormat;
    text?: string | null;
    card?: BusinessChatCardPayload | null;
    endpointId?: string | null;
  };
  /** 2026-05-28: 適用主軸カテゴリ ID 配列 (null = 共通) */
  applicableCategoryIds?: string[] | null;
  /** 2026-05-28: UI バッジ表示用 audience。未指定なら eventType 既定 */
  audience?: BusinessNotificationAudience;
}

/** 重複解消の結果 (UI で「既存テンプレ X から該当カテゴリを外しました」と通知するために返す) */
export interface UpsertConflict {
  templateId: string;
  remainingCategoryIds: string[];
  unassigned: boolean;
}

interface UpsertResult {
  templateId: string;
  eventType: BusinessMailEvent | null;
  conflicts: UpsertConflict[];
}

const upsertFn = httpsCallable<UpsertInput, UpsertResult>(
  functions,
  'upsertBusinessMailTemplate',
);

export function useUpsertBusinessMailTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpsertInput) => {
      const r = await upsertFn(input);
      return r.data;
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['business_mail_templates'] }),
  });
}

const deleteFn = httpsCallable<{ templateId: string }, { ok: true }>(
  functions,
  'deleteBusinessMailTemplate',
);

export function useDeleteBusinessMailTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (templateId: string) => {
      const r = await deleteFn({ templateId });
      return r.data;
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['business_mail_templates'] }),
  });
}
